<template>
  <div class="px-4 sm:px-0">
    <div class="flex items-center justify-between">
      <div>
        <h2 class="text-2xl font-bold tracking-tight text-gray-900">{{ $gettext("Ventes") }}</h2>
        <p class="mt-1 text-sm text-gray-500">{{ $gettext("Voici un résumé de l'ensemble des ventes.") }}</p>
      </div>
      <div v-if="saleListingAgreementStore.totalCount > 0 || saleListingAgreementStore.searchQuery || hasActiveFilters">
        <router-link :to="{ name: 'new-sale-listing-agreement' }"
          class="inline-flex items-center gap-x-1.5 rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
          <PlusIcon class="-ml-0.5 size-4" aria-hidden="true" />
          {{ $gettext("Nouvelle mission de vente") }}
        </router-link>
      </div>
    </div>

    <div class="mt-6 flow-root">
      <!-- Empty state -->
      <div v-if="saleListingAgreementStore.totalCount == 0 && !saleListingAgreementStore.searchQuery && !hasActiveFilters" class="text-center p-20">
        <p class="mt-1 text-sm text-gray-500">{{ $gettext("Désolé, mais nous ne trouvons pas de vente pour l'instant...") }}</p>
        <div class="mt-6">
          <router-link :to="{ name: 'new-sale-listing-agreement' }"
            class="inline-flex items-center gap-x-1.5 rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 transition-colors">
            <PlusIcon class="-ml-0.5 size-4" aria-hidden="true" />
            {{ $gettext("Nouvelle vente") }}
          </router-link>
        </div>
      </div>

      <!-- List -->
      <div v-else>
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center gap-3">
            <div class="relative w-80">
              <MagnifyingGlassIcon class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 size-4" />
              <input v-model="saleListingAgreementStore.searchQuery" type="text" :placeholder="$gettext('Chercher une mission de vente...')"
                class="block w-full rounded-lg border-0 py-2 pl-9 pr-3 text-sm text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-500" />
            </div>
            <ListFilters v-model="saleListingAgreementStore.filters" :filterDefinitions="filterDefinitions" />
          </div>
          <span class="text-sm text-gray-500">{{ saleListingAgreementStore.totalCount }} {{ $gettext("ventes") }}</span>
        </div>

        <div class="overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-900/5">
          <table v-if="saleListingAgreementStore.all.length > 0" class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th scope="col" class="py-3 pl-6 pr-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">{{ $gettext("Vendeur(s)") }}</th>
                <th scope="col" class="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">{{ $gettext("Propriété(s)") }}</th>
                <th scope="col" class="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">{{ $gettext("Commission") }}</th>
                <th scope="col" class="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">{{ $gettext("Date de fin") }}</th>
                <th scope="col" class="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">{{ $gettext("Statut") }}</th>
                <th scope="col" class="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">{{ $gettext("Risque") }}</th>
                <th scope="col" class="relative py-3 pl-3 pr-6"></th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr
                v-for="saleListingAgreement in saleListingAgreementStore.paginated"
                :key="saleListingAgreement.id"
                class="hover:bg-gray-50 transition-colors cursor-pointer"
                @click="$router.push({ name: 'sale-listing-agreement', params: { id: saleListingAgreement.id } })"
              >
                <SaleListingAgreementItemView :saleListingAgreement="saleListingAgreement" />
              </tr>
            </tbody>
          </table>
          <div v-else class="text-center py-20">
            <h3 class="text-sm font-semibold text-gray-900">{{ $gettext("Pas de résultats trouvés") }}</h3>
          </div>
        </div>
        <PaginationNavigation :store="saleListingAgreementStore" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted } from "vue";
import { PlusIcon } from "@heroicons/vue/20/solid";
import { MagnifyingGlassIcon } from "@heroicons/vue/24/outline";
import { useGettext } from "vue3-gettext";
import { useSaleListingAgreementStore } from "@/stores/saleListingAgreement.js";
import PaginationNavigation from "@/components/layout/PaginationNavigation.vue";
import SaleListingAgreementItemView from "@/views/sale-listing-agreements/SaleListingAgreementItemView.vue";
import ListFilters from "@/components/ui/ListFilters.vue";

const { $gettext } = useGettext();
const saleListingAgreementStore = useSaleListingAgreementStore();

const hasActiveFilters = computed(() => {
  const filters = saleListingAgreementStore.filters;
  if (!filters) return false;
  return Object.values(filters).some((v) => (Array.isArray(v) ? v.length > 0 : Boolean(v)));
});

const filterDefinitions = [
  {
    key: "transaction_status",
    label: $gettext("Statut"),
    options: [
      { value: "in_progress", label: $gettext("En cours") },
      { value: "completed", label: $gettext("Complété") },
    ],
  },
  {
    key: "risk_level",
    label: $gettext("Risque"),
    options: [
      { value: "low", label: $gettext("Faible") },
      { value: "medium", label: $gettext("Moyen") },
      { value: "high", label: $gettext("Élevé") },
      { value: "none", label: $gettext("Non évalué") },
    ],
  },
];

const overviewIncludes = [
  "saleListingAgreementSellers",
  "saleListingAgreementProperties",
  "commissionType",
  "properties",
  "physicalPersonSellers",
  "companySellers",
  "transactionStatus",
  "riskLevel"
];

onMounted(async () => {
  await saleListingAgreementStore.fetchAll(null, overviewIncludes);
  await saleListingAgreementStore.watchResourceChanges(() => {
    saleListingAgreementStore.fetchAll(null, overviewIncludes);
  });
});
</script>
